import type { V2Pharmacy } from '../../types/api-v2.types';
import type { PharmacyResponse } from '../../types/api-v0.types';
import type { AppState, Dispatch } from '../../types/app.types';
import * as React from 'react';
import get from 'lodash/get';

import { useDispatch } from 'react-redux';
import Text from '../text';
import Cell from '../cell';
import { Color } from '../../styles/colors';
import { H, V } from '../layout';
import Button from '../button';
import Input from '../form/core/input';
import Separator from '../atoms/separator';
import { GradientBlock } from '../gradient';
import { trackClick } from '../../utils/analytics';
import { blinkAPIClient } from '../../utils/blink-api-client';
import { formatPharmacyAddress } from '../../utils/format-address';
import { openModal } from '../../store/modules/modal-module';
import { connect, ConnectedProps } from 'react-redux';
import handleEnterKey from '../../utils/handle-enter-key';
import InformationIcon from '../../assets/icons/information';
import type { PricingTierAccessDetail } from '../../store/modules/available-pricing-tiers-module';
import RadioButton from '../../components/atoms/radio-button';
import PreferredPharmacyModal from './preferred-pharmacy-modal';
import PharmacyIcon from '../../assets/icons/pharmacy';
import { withRouter, RouteComponentProps } from 'react-router';

export const PHARMACY_DISPLAY_LIMIT = 10;

type State = {
  loading: boolean;
  pharmacies: Array<V2Pharmacy>;
  zipCode: string | null | undefined;
  selectedPharmacy: V2Pharmacy | null | undefined;
  inputError: string | null | undefined;
  showAll: Record<string, boolean>;
};

type Props = RouteComponentProps &
  PropsFromRedux & {
    onContinue?: (pharmacy: V2Pharmacy | null | undefined) => () => void;
    onSelection?: (pharmacy: V2Pharmacy) => void;

    /**
     * Search strategy enum:
     * - preferLocationAPI: tries the location api and falls back to backend geoIP if blocked
     * - locationAPI: tries the location api and shows chains if blocked
     * - geoIP: geoIP only (will not ask for location from user)
     **/
    searchStrategy?: 'preferLocationAPI' | 'locationAPI' | 'geoIP' | 'searchByZipCode';
    showOutOfNetwork?: boolean;
    onlySupersaver?: boolean;
    isQuickSave?: boolean;
    magicTier?: PricingTierAccessDetail | null | undefined;
    numberOfResults?: number;
    filter?: string[];
    zipCode?: string | null | undefined;
  };

class PharmacySearch extends React.Component<Props, State> {
  state: State = {
    loading: false,
    pharmacies: [],
    zipCode: null,
    showAll: {
      edlp: false,
      quickSave: false,
      all: false,
    },
    inputError: null,
    selectedPharmacy: null,
  };
  static defaultProps: Partial<Props> = {
    showOutOfNetwork: false,
    searchStrategy: 'geoIP',
    numberOfResults: PHARMACY_DISPLAY_LIMIT,
    onlySupersaver: false,
    isQuickSave: false,
    magicTier: null,
  };
  trackEvent = () => {
    const { dispatch, isLoggedIn, userLocation } = this.props;
    dispatch(
      trackClick('See More Pharmacies', 'link', {
        isLoggedIn: isLoggedIn,
        zipCode: userLocation && userLocation.zipCode,
        stateCode: userLocation && userLocation.stateCode,
      })
    );
  };
  fetchPharmacies = ({
    lat,
    lng,
    zipCode,
    filter,
    showOutOfNetwork = false,
    onlySupersaver = false,
    searchNetworkIds,
  }: {
    zipCode?: string;
    lat?: number;
    lng?: number;
    filter?: string[];
    showOutOfNetwork: boolean;
    onlySupersaver: boolean;
    searchNetworkIds?: string | null | undefined;
  }) => {
    // TODO Memo: Refactor this with query-string
    let endpoint = '/v2/pharmacies?';
    if (lat && lng) endpoint += `lat=${lat}&lng=${lng}`;
    else if (zipCode) endpoint += `zip_code=${zipCode}`;
    if (showOutOfNetwork) endpoint += `&allow_out_of_network=${showOutOfNetwork ? 1 : 0}`;
    if (onlySupersaver) endpoint += `&only_supersaver=${onlySupersaver ? 1 : 0}`;
    if (searchNetworkIds) endpoint += `&network_ids=${searchNetworkIds}`;
    if (filter) endpoint += `&brands=${filter.join(',')}`;
    this.setState(
      {
        loading: true,
      },
      () => {
        blinkAPIClient
          .get(endpoint)
          .then((res) => res.data)
          .then((data: PharmacyResponse) => {
            this.setState({
              pharmacies: data.result.results,
              loading: false,
              zipCode: data.result.zipCode,
            });
          })
          .catch(() => {
            this.setState({
              loading: false,
            });
          });
      }
    );
  };
  searchByGeoIP = () => {
    this.fetchPharmacies({
      showOutOfNetwork: Boolean(this.props.showOutOfNetwork),
      onlySupersaver: Boolean(this.props.onlySupersaver),
      searchNetworkIds:
        this.props.isQuickSave && this.props.magicTier ? `${this.props.magicTier.pharmacyNetworkId}` : null,
      filter: this.props.filter,
    });
  };
  searchByLocationAPI = (fallbackOnGeoIp: boolean) => {
    if (!navigator.geolocation) {
      return fallbackOnGeoIp ? this.searchByGeoIP() : null;
    }

    const opts = {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: Infinity,
    };
    this.setState(
      {
        loading: true,
      },
      () => {
        navigator.geolocation.getCurrentPosition(
          (res) => {
            this.props.dispatch(
              trackClick('User current position', 'alert', {
                accepted: true,
                ...res.coords,
              })
            );
            this.fetchPharmacies({
              lat: res.coords.latitude,
              lng: res.coords.longitude,
              showOutOfNetwork: Boolean(this.props.showOutOfNetwork),
              onlySupersaver: Boolean(this.props.onlySupersaver),
              searchNetworkIds:
                this.props.isQuickSave && this.props.magicTier ? `${this.props.magicTier.pharmacyNetworkId}` : null,
              filter: this.props.filter,
            });
          },
          () => {
            this.props.dispatch(
              trackClick('User current position', 'alert', {
                accepted: false,
              })
            );

            if (this.props.searchStrategy === 'preferLocationAPI') {
              this.searchByGeoIP();
            } else {
              this.setState({
                loading: false,
              });
            }
          },
          opts
        );
      }
    );
  };
  changeZipCode = (zipCode: string) => {
    if (zipCode && zipCode.length === 5 && /^\d+$/.test(zipCode)) {
      this.setState(
        {
          zipCode: zipCode,
        },
        () => {
          this.fetchPharmacies({
            zipCode: zipCode,
            showOutOfNetwork: Boolean(this.props.showOutOfNetwork),
            onlySupersaver: Boolean(this.props.onlySupersaver),
            searchNetworkIds:
              this.props.isQuickSave && this.props.magicTier ? `${this.props.magicTier.pharmacyNetworkId}` : null,
            filter: this.props.filter,
          });
        }
      );
    } else if (zipCode) {
      this.setState({
        inputError: 'Invalid zipcode',
      });
    }
  };
  searchByZipCode = (zipCode: string | null | undefined) => {
    if (zipCode) {
      this.setState(
        {
          zipCode: zipCode,
        },
        () => {
          this.fetchPharmacies({
            zipCode: zipCode,
            showOutOfNetwork: Boolean(this.props.showOutOfNetwork),
            onlySupersaver: Boolean(this.props.onlySupersaver),
            searchNetworkIds:
              this.props.isQuickSave && this.props.magicTier ? `${this.props.magicTier.pharmacyNetworkId}` : null,
            filter: this.props.filter,
          });
        }
      );
    } else {
      this.searchByGeoIP();
    }
  };
  showAll = (tier: string) => {
    const state = this.state.showAll;
    this.setState({
      showAll: { ...state, [tier]: true },
    });
    this.trackEvent();
  };

  componentDidMount() {
    switch (this.props.searchStrategy) {
      case 'geoIP':
        return this.searchByGeoIP();

      case 'preferLocationAPI':
        return this.searchByLocationAPI(true);

      case 'searchByZipCode':
        return this.searchByZipCode(this.props.zipCode);

      default:
        return this.searchByLocationAPI(false);
    }
  }

  onSelection = (pharmacy: V2Pharmacy) => () => {
    this.setState({
      selectedPharmacy: pharmacy,
    });
    // Execute onSelection passed through props
    const { onSelection } = this.props;
    if (onSelection) onSelection(pharmacy);
  };

  render() {
    const { dispatch, onContinue, searchStrategy, numberOfResults }: Props = this.props;
    const { loading, pharmacies, showAll, zipCode, inputError, selectedPharmacy }: State = this.state;
    const shouldShowPharmacies =
      zipCode && !loading && (pharmacies.length >= 0 || searchStrategy === 'preferLocationAPI');
    // they've given location/entered zip, but no results arrived
    const displayLimit = numberOfResults || PHARMACY_DISPLAY_LIMIT;
    const pharmaciesToShow = showAll.all ? pharmacies : pharmacies.slice(0, displayLimit);
    const displayShowMore = pharmacies && pharmacies.length > displayLimit && !showAll.all;
    return (
      <div>
        <div className="col-sm-12 px-sm-0 mt-sm-16 mb-sm-24">
          <Input
            type="text"
            name="pharmacyZipCode"
            placeholder={zipCode ? `Pharmacies near ${zipCode}` : 'Enter zip code'}
            onChange={(value) => this.changeZipCode(value)}
            debounce={500}
            error={inputError}
            maxLength={5}
            inputStyle={{
              margin: 0,
            }}
          />
        </div>

        {loading && <LoadingGradients />}

        <div
          style={{
            maxHeight: 507,
            overflowY: 'scroll',
          }}
        >
          {shouldShowPharmacies &&
            pharmaciesToShow.map((pharmacy, idx) => (
              <SelectablePharmacyRow
                key={`pharmacy-row-${idx}`}
                dispatch={dispatch}
                pharmacy={pharmacy}
                onSelection={this.onSelection(pharmacy)}
                isSelected={pharmacy.npi === (selectedPharmacy && selectedPharmacy.npi)}
              />
            ))}

          {shouldShowPharmacies && displayShowMore && <SeeMorePharmacies showAll={() => this.showAll('all')} />}

          {shouldShowPharmacies && pharmacies.length === 0 && (
            <Cell noIcon>
              <Text
                el="p"
                type="body2"
                style={{
                  marginTop: 12,
                }}
              >
                We didn&apos;t find any nearby pharmacies. Try searching in a different zip code.
              </Text>
            </Cell>
          )}
        </div>
        {onContinue && (
          <React.Fragment>
            <Separator className="mt-sm-20 mb-sm-24" />
            <div className="d-sm-flex justify-content-sm-end">
              <div className="col-sm-12 col-md-3 px-sm-0">
                <Button
                  eventName="Select Pharmacy"
                  eventData={{
                    pharmacy: selectedPharmacy,
                  }}
                  disabled={!selectedPharmacy}
                  onClick={onContinue(selectedPharmacy)}
                  fullWidth
                >
                  Continue
                </Button>
              </div>
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }
}

const SelectablePharmacyRow = ({
  pharmacy,
  onSelection,
  isSelected,
}: {
  dispatch: Dispatch;
  pharmacy: V2Pharmacy;
  onSelection: (...args: Array<any>) => any;
  isSelected: boolean;
}) => {
  const address = formatPharmacyAddress(pharmacy, {
    excludeZip: true,
  });
  const zipCode = pharmacy.location && pharmacy.location.address.zipCode;
  const formattedName = pharmacy.brand ? pharmacy.brand.formattedName : pharmacy.name;
  const isPreferred = pharmacy.isPreferred;
  const distance = pharmacy.location && pharmacy.location.distance;
  return (
    <div
      className="d-sm-flex flex-sm-column col-sm-12 px-sm-0 py-sm-20"
      style={{
        borderTop: `1px solid ${Color.tertiary20}`,
      }}
    >
      {isPreferred && <PreferredPharmacyLabel pharmacy={pharmacy} showInfoButton={false} />}
      <div className="d-sm-flex flex-sm-row align-items-sm-center">
        <RadioButton
          name={pharmacy.name}
          value={`pharmacy-${pharmacy.npi}`}
          checked={isSelected}
          onChecked={onSelection}
        >
          <div
            className="d-sm-flex flex-sm-column align-items-sm-start"
            style={{
              width: '100%',
            }}
          >
            <div className="d-sm-flex flex-sm-row justify-content-sm-between align-items-sm-center col-sm-12 px-sm-0">
              <Text
                type="list1"
                style={{
                  marginBottom: 0,
                }}
              >
                {formattedName}&nbsp;
              </Text>
              <Text type="headline-blinkui" className="ml-sm-12">
                {distance}
              </Text>
            </div>
            <div className="d-sm-flex flex-sm-column align-items-sm-start flex-md-row justify-content-md-center">
              <Text key="address" type="body2-blinkui">
                {address}&nbsp;
              </Text>
              <Text type="body2-blinkui">{zipCode}</Text>
            </div>
          </div>
        </RadioButton>
      </div>
    </div>
  );
};

export const PharmacyRow = ({
  pharmacy,
  onClick,
  clickIcon,
  showPreferredInfoButton,
  noPharmacyIcon,
}: {
  pharmacy: V2Pharmacy;
  onClick?: () => void;
  clickIcon?: string;
  showPreferredInfoButton?: boolean;
  noPharmacyIcon?: boolean;
}) => {
  const address = get(pharmacy, 'location.address', {});
  const { street1 = '', city = '', state = '', zipCode = '' } = address;
  const addressString = `${street1}, ${city} ${state}`;
  const isPreferred = get(pharmacy, 'isPreferred', false);
  const onRowClick = clickIcon ? onClick : undefined;

  return (
    <div
      className="d-sm-flex flex-sm-column"
      style={{
        width: '100%',
      }}
    >
      {isPreferred && <PreferredPharmacyLabel pharmacy={pharmacy} showInfoButton={showPreferredInfoButton} />}
      <div
        className="d-sm-flex flex-sm-row align-items-sm-center justify-content-sm-between"
        onClick={onRowClick}
        role="button"
        onKeyDown={handleEnterKey(onRowClick)}
        tabIndex={0}
        data-user1st-id="cf-pharmacy-row"
      >
        <div className="d-sm-flex flex-sm-row align-items-sm-center">
          {!noPharmacyIcon && (
            <PharmacyIcon
              style={{
                marginRight: 8,
              }}
            />
          )}
          <div className="d-sm-flex flex-sm-column align-items-sm-start">
            <Text type="body3-blinkui" align="left">
              {get(pharmacy, 'name', '')}
            </Text>
            <div className="d-sm-flex flex-sm-column align-items-sm-start flex-md-row align-items-md-center">
              <Text type="body3-blinkui" color={Color.tertiary80} align="left">
                {addressString} &nbsp;
              </Text>
              <Text type="body3-blinkui" color={Color.tertiary80}>
                {zipCode} &nbsp;
              </Text>
            </div>
            {onClick && !clickIcon && (
              <div onClick={onClick} role="button" onKeyDown={handleEnterKey(onClick)} tabIndex={0}>
                <Text
                  style={{
                    color: Color.softBlue,
                    cursor: 'pointer',
                  }}
                >
                  Change
                </Text>
              </div>
            )}
          </div>
        </div>
        {onClick && clickIcon && <img src={clickIcon} alt="select-pharmacy" />}
      </div>
    </div>
  );
};
export const PreferredPharmacyLabel = ({
  pharmacy,
  showInfoButton = true,
}: {
  pharmacy: V2Pharmacy;
  showInfoButton?: boolean;
}) => {
  const dispatch = useDispatch();

  const onInfoClick = (event) => {
    event.stopPropagation();
    dispatch(openModal('preferred-pharmacy'));
  };

  return (
    <div
      className="d-sm-flex align-items-sm-center py-sm-4 px-sm-8 mb-sm-8"
      style={{
        width: showInfoButton ? 170 : 142,
        borderRadius: 4,
        backgroundColor: Color.softBlueLight,
      }}
      data-user1st-id="cf-preferred-pharmacy"
    >
      <Text
        type="hint"
        style={{
          margin: 0,
        }}
      >
        Preferred Pharmacy
      </Text>
      {showInfoButton && (
        <span
          style={{
            height: 24,
            width: 24,
          }}
          onClick={onInfoClick}
          role="button"
          tabIndex={0}
          id="open-preferred-pharmacy-modal"
          onKeyDown={handleEnterKey(onInfoClick)}
        >
          <InformationIcon
            style={{
              marginLeft: 4,
              cursor: 'pointer',
            }}
          />
        </span>
      )}
      <PreferredPharmacyModal dispatch={dispatch} pharmacy={pharmacy} />
    </div>
  );
};
export const PreferredPharmacyLabelSmall = ({
  pharmacy,
  showInfoButton = true,
}: {
  pharmacy: V2Pharmacy;
  showInfoButton?: boolean;
}) => {
  const dispatch = useDispatch();

  const onInfoClick = (event) => {
    event.stopPropagation();
    dispatch(openModal('preferred-pharmacy'));
  };

  return (
    <div
      className="d-sm-flex align-items-sm-center py-sm-4 px-sm-8 mb-sm-4"
      style={{
        width: showInfoButton ? 150 : 126,
        borderRadius: 4,
        backgroundColor: Color.softBlueLight,
      }}
    >
      <Text
        type="hint"
        style={{
          margin: 0,
          fontSize: '0.765rem',
        }}
      >
        Preferred Pharmacy
      </Text>
      {showInfoButton && (
        <span
          style={{
            height: 12,
            width: 12,
          }}
          onClick={onInfoClick}
          role="button"
          tabIndex={0}
          id="open-preferred-pharmacy-modal"
          onKeyDown={handleEnterKey(onInfoClick)}
        >
          <InformationIcon
            style={{
              marginLeft: 4,
              marginTop: -3,
              cursor: 'pointer',
              width: 18,
              height: 18,
            }}
          />
        </span>
      )}
      <PreferredPharmacyModal dispatch={dispatch} pharmacy={pharmacy} />
    </div>
  );
};
export const GradientPharmacy = () => (
  <Cell noIcon>
    <H
      justifyContent="flex-start"
      style={{
        marginTop: 4,
        marginBottom: 3,
      }}
    >
      <GradientBlock width="30px" height="30px" margin="0 18px 0 0" />
      <V>
        <GradientBlock width="168px" height="18px" margin="0 0 10px 0" />
        <GradientBlock width="238px" height="12px" />
      </V>
    </H>
  </Cell>
);

const LoadingGradients = () => (
  <div>
    <GradientPharmacy />
    <GradientPharmacy />
    <GradientPharmacy />
    <GradientPharmacy />
    <GradientPharmacy />
  </div>
);

const SeeMorePharmacies = ({ showAll }) => (
  <div
    className="pt-sm-20"
    style={{
      borderTop: `1px solid ${Color.tertiary20}`,
    }}
    data-user1st-id="see-more-pharmacies"
  >
    <span
      style={{
        marginTop: 3,
        marginBottom: 3,
        cursor: 'pointer',
      }}
      role="button"
      onClick={showAll}
      tabIndex={0}
      onKeyDown={handleEnterKey(showAll)}
      data-qa="see-more-pharmacies"
    >
      <Text
        type="body2-blinkui"
        style={{
          color: Color.link100,
          marginBottom: 0,
        }}
      >
        See More Pharmacies
      </Text>
    </span>
  </div>
);

const stateToProps = (state: AppState) => ({
  userLocation: state.account.userLocation,
  isLoggedIn: state.session.authenticated,
});

const dispatchToProps = (dispatch: Dispatch) => ({
  dispatch,
});

const connector = connect(stateToProps, dispatchToProps);

export type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(withRouter(PharmacySearch));
